import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
  IconSearch
} from "@tabler/icons-react";

import { successNotification } from "@app/common/components/notifications";
import * as DrugDeliveryHooks from "@app/services/drugDelivery/hooks";
import {
  Lot,
  LotsScannerResponse,
  Medicine,
  TopInventoryFilters
} from "@app/services/drugDelivery/types";
import { useGenericData } from "@app/store/useGenericData";
import { useCallback, useEffect, useState } from "react";
import "../styles.scss";
import InventoryStockView from "./InventoryStockView";
import TopInventoryView from "./TopInventoryView";

type InventorySource = "ownInventory" | "topInventory";

const getDefaultInventorySource = (
  deliveryBy?: "insurance" | "freeSale" | "freeSaleWithOrder" | "ownInventory" | null
): InventorySource => {
  return deliveryBy === "insurance" ? "ownInventory" : "topInventory";
};

export default function MedicationScanner({
  isOpen,
  onClose,
  orderId,
  handleChange,
  dataToValidate
}: {
  orderId: number;
  patientId?: number;
  externalClientId?: number;
  isOpen: boolean;
  handleChange: (data: LotsScannerResponse) => void;
  dataToValidate: {
    dataOrder?: Medicine[];
    deliveryBy?: "insurance" | "freeSale" | "freeSaleWithOrder" | "ownInventory" | null;
    fullDeliveryIsApproved?: boolean;
  };
  dispensaryId?: number;
  consumptionPointId?: number;
  isOwnInventory?: boolean;
  onClose: () => void;
}) {
  const [barcode, setBarcode] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [inventorySource, setInventorySource] = useState<InventorySource>(() =>
    getDefaultInventorySource(dataToValidate.deliveryBy)
  );
  const [selectedMedicineId, setSelectedMedicineId] = useState<number | null>(null);
  const [localQty, setLocalQty] = useState<Record<string, number>>({});
  const [deletedLots, setDeletedLots] = useState<Set<string>>(new Set());

  const siteId = useGenericData((state) => state.genericData.siteId);

  const { data: inventoryStockData } = DrugDeliveryHooks.useGetInventoryStock(
    {
      barcode: Number(barcode),
      sedeId: siteId ?? 0,
      medicineId: selectedMedicineId ?? 0
    },
    inventorySource === "ownInventory" &&
      barcode !== null &&
      barcode !== "" &&
      siteId !== null &&
      selectedMedicineId !== null
  );

  const isNumericSearch = /^\d+$/.test(barcode ?? "");
  const topInventoryFilters: TopInventoryFilters = {
    siteId: siteId ?? 0,
    orderId,
    ...(barcode && isNumericSearch ? { barcode } : {}),
    ...(barcode && !isNumericSearch ? { articleName: barcode } : {})
  };

  const { data: topInventoryData } = DrugDeliveryHooks.useGetTopInventory(
    topInventoryFilters,
    inventorySource === "topInventory" && !!barcode
  );

  const resetTopInventoryState = useCallback(() => {
    setLocalQty({});
    setDeletedLots(new Set());
  }, []);

  const handleSearch = () => {
    setBarcode(inputValue.trim() || null);
  };

  const handleClearSearch = () => {
    setBarcode(null);
    setInputValue("");
    setSelectedMedicineId(null);
  };

  useEffect(() => {
    resetTopInventoryState();
    setBarcode(null);
    setInputValue("");
    if (inventorySource !== "ownInventory") {
      setSelectedMedicineId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventorySource]);

  useEffect(() => {
    if (dataToValidate.deliveryBy !== "insurance" && inventorySource === "ownInventory") {
      setInventorySource("topInventory");
    }
  }, [dataToValidate.deliveryBy, inventorySource]);

  const closeModal = () => {
    onClose();
    resetState();
  };

  const resetState = () => {
    setBarcode(null);
    setInputValue("");
    setInventorySource(getDefaultInventorySource(dataToValidate.deliveryBy));
    setSelectedMedicineId(null);
    setLocalQty({});
    setDeletedLots(new Set());
  };

  const handleTopIncrease = (key: string, maxStock: number) => {
    setLocalQty((prev) => ({ ...prev, [key]: Math.min((prev[key] ?? 0) + 1, maxStock) }));
  };

  const handleTopDecrease = (key: string) => {
    setLocalQty((prev) => ({ ...prev, [key]: Math.max((prev[key] ?? 0) - 1, 0) }));
  };

  const handleTopDelete = (key: string) => {
    setDeletedLots((prev) => new Set([...prev, key]));
  };

  const handleTopSetQty = (key: string, value: number, maxStock: number) => {
    const clamped = Math.min(Math.max(value, 0), maxStock);
    setLocalQty((prev) => ({ ...prev, [key]: clamped }));
  };

  const handleAcceptTopInventory = () => {
    const topData = topInventoryData?.results ?? [];

    const selectedLots: Lot[] = [];
    topData.forEach((article) => {
      article.lots.forEach((lot, lotIndex) => {
        const key = `${article.articleId}-${lotIndex}`;
        const qty = localQty[key] ?? 0;
        if (!deletedLots.has(key) && qty > 0) {
          selectedLots.push({
            id: article.articleId * 100 + lotIndex,
            articleId: article.articleId,
            warehouseId: lot.lotName,
            expirationDate: lot.expirationDate,
            qtyAvailable: lot.availableStock,
            lotName: article.articleName,
            qtyUsed: qty,
            medicineId: article.medicineId,
            warehouseCode: lot.warehouseCode ?? "",
            warehouseName: lot.warehouseName ?? ""
          });
        }
      });
    });

    if (selectedLots.length > 0) {
      handleChange({
        id: "",
        barcode: barcode ?? "",
        description: "",
        manufacturer: "",
        salesPrice: "",
        medicationOrderId: "",
        OrderId: String(orderId),
        medicineId: selectedLots[0]?.medicineId ?? "",
        medicineCUM: "",
        medicineInvima: "",
        isTop: true,
        lots: selectedLots
      });
    }

    closeModal();
  };

  return (
    <Modal
      w="45%"
      size={1000}
      title="Escanear medicamentos"
      opened={isOpen}
      onClose={closeModal}
      data-testid="medicationscanner-modal"
    >
      <Stack gap="md" data-testid="medicationscanner-header">
        <Box>
          <Group gap={6} mb="xs">
            <Text fw={600} size="sm">
              1. Seleccione el tipo de inventario
            </Text>
            <IconInfoCircle size={14} color="#868e96" />
          </Group>
          <SegmentedControl
            color="brand-color"
            value={inventorySource}
            onChange={(value) => setInventorySource(value as InventorySource)}
            data-testid="medicationscanner-inventory-source"
            data={[
              ...(dataToValidate.deliveryBy === "insurance"
                ? [{ value: "ownInventory", label: "Inventario propio" }]
                : []),
              { value: "topInventory", label: "Inventario TOP" }
            ]}
          />
        </Box>

        <Box>
          <Text fw={600} size="sm" mb="xs">
            2. Escanee o busque el medicamento
          </Text>
          {inventorySource === "ownInventory" && (
            <Select
              label="Medicamento"
              placeholder="Seleccionar medicamento..."
              data-testid="medicationscanner-medicine-select"
              mb="sm"
              data={(dataToValidate.dataOrder ?? [])
                .filter((med) => med.id !== undefined)
                .map((med) => ({
                  value: String(med.id),
                  label: med.name ?? ""
                }))}
              value={selectedMedicineId !== null ? String(selectedMedicineId) : null}
              onChange={(value) => setSelectedMedicineId(value !== null ? Number(value) : null)}
            />
          )}
          <Group gap="xs" align="flex-end">
            <TextInput
              flex={1}
              value={inputValue}
              placeholder="Escanee o escriba código de barras o nombre del medicamento..."
              autoFocus
              data-testid="medicationscanner-barcode-input"
              onChange={({ target }) => setInputValue(target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <ActionIcon
              variant="filled"
              color="#00b4cc"
              size="lg"
              onClick={handleSearch}
              data-testid="medicationscanner-search-button"
              title="Buscar"
            >
              <IconSearch size={18} />
            </ActionIcon>
          </Group>
        </Box>

        <Box>
          <Group justify="space-between" mb="xs" align="flex-start">
            <Text fw={600} size="sm">
              3. Seleccione el artículo, lote y cantidad a entregar
            </Text>
            <Group gap="xs" align="center">
              {inventorySource === "topInventory" && (
                <Group gap={4}>
                  <IconInfoCircle size={14} color="#868e96" />
                  <Text size="xs" c="dimmed">
                    Se muestran los genéricos asociados al medicamento buscado
                  </Text>
                </Group>
              )}
            </Group>
          </Group>

          <Box data-testid="medicationscanner-table-container">
            {inventorySource === "ownInventory" ? (
              <InventoryStockView
                data={inventoryStockData?.results ?? null}
                isLoading={false}
                selectedMedicine={
                  selectedMedicineId !== null
                    ? (dataToValidate.dataOrder?.find((med) => med.id === selectedMedicineId) ??
                      null)
                    : null
                }
                onConfirmArticle={(payload) => {
                  successNotification(
                    "Artículo confirmado exitosamente",
                    `Se confirmó el artículo: ${payload.tradename} x${payload.qty}`
                  );
                }}
              />
            ) : (
              <TopInventoryView
                data={topInventoryData?.results ?? []}
                isLoading={false}
                localQty={localQty}
                deletedLots={deletedLots}
                deliveryBy={dataToValidate.deliveryBy}
                onIncrease={handleTopIncrease}
                onDecrease={handleTopDecrease}
                onDelete={handleTopDelete}
                onSetQty={handleTopSetQty}
              />
            )}
          </Box>
        </Box>

        <>
          <Divider />
          <Box>
            {inventorySource !== "topInventory" && (
              <Group gap="xs" mb={6}>
                <IconCircleCheck size={16} color="green" />
                <Text size="xs">
                  El check (verde) se habilita cuando la cantidad a entregar es mayor a 0.
                </Text>
              </Group>
            )}
            <Group gap="xs" wrap="nowrap">
              <Box style={{ flexShrink: 0 }}>
                <IconAlertTriangle size={16} color="orange" />
              </Box>
              <Text size="xs">
                Si la cantidad a entregar es mayor a la cantidad ordenada (aplica para ventas por
                aseguradora), se mostrará una advertencia al confirmar.
              </Text>
            </Group>
          </Box>
        </>

        <Group justify="space-between">
          <Button
            variant="outline"
            color="secondary-color"
            onClick={handleClearSearch}
            data-testid="medicationscanner-clear-button"
          >
            Limpiar búsqueda
          </Button>
          {inventorySource !== "topInventory" ? (
            <Button
              variant="outline"
              onClick={closeModal}
              data-testid="medicationscanner-cancel-button"
            >
              Cancelar
            </Button>
          ) : (
            <Button
              color="#00b4cc"
              onClick={handleAcceptTopInventory}
              data-testid="medicationscanner-accept-button"
            >
              Aceptar
            </Button>
          )}
        </Group>

        {inventorySource === "ownInventory" && (
          <Box bg="#f1f3f5" p="xs" style={{ borderRadius: 4 }}>
            <Group gap="xs" align="flex-start">
              <Box style={{ flexShrink: 0 }}>
                <IconInfoCircle size={14} color="#868e96" />
              </Box>
              <Text size="xs" c="dimmed">
                El inventario consultado corresponde al punto de consumo asociado al centro de
                consumo, centro de costo y sede.
              </Text>
            </Group>
          </Box>
        )}
      </Stack>
    </Modal>
  );
}
